// AI Feature Types for FinHarv Admin Panel

// Analysis Types
export type AnalysisType = 'root_cause' | 'fish_diagnosis'
export type AnalysisStatus = 'pending' | 'analyzing' | 'completed' | 'failed'

export type AnomalyCategory =
  | 'water_quality'
  | 'mortality'
  | 'growth'
  | 'behavioral'
  | 'equipment'
  | 'environmental'

// Trigger record types for root cause analysis
export type TriggerRecordType = 'water_quality' | 'health_check' | 'feed_record' | 'maintenance_check'

export type InputMode = 'photo' | 'photo_symptoms' | 'text'
export type Severity = 'none' | 'low' | 'medium' | 'high' | 'critical'
export type FindingStatus = 'all_clear' | 'issue_found'

// Root Cause Analysis
export interface RootCauseAnalysisRequest {
  pondId: string
  triggerRecordType?: TriggerRecordType  // Optional: type of record that triggered analysis
  triggerRecordId?: string                // Optional: ID of the specific record
  description?: string                    // User's description of concern
  lookbackDays?: number                   // Analysis window (default 30)
}

// Referenced Health Check data for AI diagnosis
export interface ReferencedHealthCheck {
  checkId: string
  checkedAt: string
  healthScore?: number
  riskLevel?: string
  averageWeight: number
  sampleSize: number
  mortalityCount?: number
  mortalityRate?: number
  feedConversionRatio?: number
  physicalSymptoms?: string
  behaviorObservations?: string
  observations?: string
  parasiteCode?: string
  parasiteName?: string
  treatmentApplied?: string
  imageIds?: string[]
}

// Fish Health Diagnosis
export interface FishDiagnosisRequest {
  pondId: string
  inputMode: InputMode
  imageIds?: string[]
  symptoms?: string[]
  description?: string
  fishSpecies?: string
  affectedCount?: number
  duration?: string
  // Referenced health checks for enhanced diagnosis
  referencedHealthChecks?: ReferencedHealthCheck[]
}

// Follow-up Question
export interface FollowUpRequest {
  analysisId: string
  question: string
}

// Recommendation
export interface Recommendation {
  priority: number
  category: string
  action: string
  reason: string
  timeframe?: string
  cost?: string
}

// Timeline Event
export interface TimelineEvent {
  timestamp: string
  eventType: string
  title: string
  description: string
  severity: Severity
  recordId?: string
  recordType?: string
  metrics?: Record<string, unknown>
}

// Conversation Message
export interface ConversationMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  tokensUsed: number
  createdAt: string
}

// Fish Impact Assessment (for root cause analysis)
// Supports both camelCase and snake_case fields for backend compatibility
export interface FishImpact {
  // camelCase (preferred)
  ifUnaddressed?: string            // What will happen to the fish if not addressed
  timelineToImpact?: string         // How quickly problems will manifest
  affectedFishPercentage?: string   // Estimated % of stock at risk
  reversible?: boolean              // Whether the damage is reversible

  // snake_case variants from backend
  if_unaddressed?: string
  timeline_to_impact?: string
  affected_fish_percentage?: string
}

// Detailed Analysis (flexible JSON structure from AI)
// Supports both camelCase and snake_case fields for backend compatibility
export interface DetailedAnalysis {
  // Common fields
  primaryCause?: string
  contributingFactors?: string[]
  evidence?: string
  confidenceBreakdown?: Record<string, number>

  // Root Cause specific fields (camelCase)
  whatHappened?: string
  whyItHappened?: string
  rootCauseChain?: string[]
  keyObservations?: string[]
  positiveIndicators?: string[]
  dataPointsAnalyzed?: number

  // Fish Diagnosis specific fields (camelCase)
  identifiedCondition?: string
  diagnosis?: string
  differentialDiagnosis?: string[]
  symptomsMatched?: string[]
  symptomsMissing?: string[]
  treatmentProtocol?: string
  preventionMeasures?: string[]

  // snake_case variants from backend (for backward compatibility)
  what_happened?: string
  why_it_happened?: string
  root_cause_chain?: string[]
  key_observations?: string[]
  positive_indicators?: string[]
  data_points_analyzed?: number
  differential_diagnoses?: string[]
  symptoms_matched?: string[]
  symptoms_missing?: string[]

  // Allow additional fields from AI
  [key: string]: unknown
}

// Full Analysis Response
export interface AIAnalysisResponse {
  id: string
  farmId: string
  pondId: string
  userId: string
  createdByName?: string
  analysisType: AnalysisType
  status: AnalysisStatus
  modelUsed: string
  createdAt: string

  // Root Cause specific
  triggerRecordType?: TriggerRecordType
  triggerRecordId?: string
  description?: string        // User's description of the concern
  lookbackDays?: number       // Analysis window in days
  // Legacy fields (for backwards compatibility with old analyses)
  anomalyCategory?: AnomalyCategory
  triggerMetric?: string
  triggerValue?: number

  // Fish Diagnosis specific
  inputMode?: InputMode
  fishSpecies?: string
  affectedCount?: number
  identifiedCondition?: string
  parasiteCode?: string
  imageIds?: string[]

  // Common results
  summary: string
  confidenceScore?: number
  severity?: Severity
  findingStatus?: FindingStatus
  detailedAnalysis?: DetailedAnalysis           // Parsed object (from REST API)
  detailedAnalysisJson?: string                 // JSON string (from ConnectRPC/proto)
  fishImpact?: FishImpact
  recommendations: Recommendation[]
  timelineEvents: TimelineEvent[]
  conversation: ConversationMessage[]

  // Error handling
  errorMessage?: string
}

// Rate Limit Response
export interface RateLimitResponse {
  analysisCount: number
  dailyLimit: number
  remaining: number
  canAnalyze: boolean
  resetsAt: string
}

// Fish Species Reference
export interface FishSpecies {
  code: string
  commonName: string
  scientificName?: string
  category?: string
  commonDiseases: string[]
  regionHints: string[]
  isActive: boolean
}

// List Request Parameters
export interface AIAnalysisListParams {
  farmId: string
  pondId?: string
  analysisType?: AnalysisType
  anomalyCategory?: AnomalyCategory
  status?: AnalysisStatus
  startDate?: string
  endDate?: string
  cursor?: string
  limit?: number
}

// List Response
export interface AIAnalysisListResponse {
  analyses: AIAnalysisResponse[]
  hasNext: boolean
  nextCursor?: string
  count: number
}

// Streaming Chunk Types
export type StreamChunkType = 'content' | 'status' | 'error' | 'complete'

export interface AIStreamChunk {
  type: StreamChunkType
  content?: string
  status?: string
  error?: string
  final?: AIAnalysisResponse
}

// UI State Types
export interface AIPageState {
  activeAnalysis: AIAnalysisResponse | null
  isStreaming: boolean
  streamedContent: string
  selectedPondId: string | null
  selectedCategory: AnomalyCategory | null
}

// Form State for Root Cause Analyzer
export interface RootCauseFormState {
  pondId: string
  recordType: TriggerRecordType | ''       // Empty string = no record selected
  selectedRecordId: string                  // ID of selected record
  description: string
  lookbackDays: number
}

// Form State for Fish Diagnosis
export interface FishDiagnosisFormState {
  pondId: string
  inputMode: InputMode
  imageFiles: File[]
  symptoms: string[]
  description: string
  fishSpecies: string
  affectedCount: string
  duration: string
}

// Symptom Options for Fish Diagnosis
export const SYMPTOM_OPTIONS = [
  'Loss of appetite',
  'Lethargy',
  'Abnormal swimming',
  'Surface gasping',
  'Discoloration',
  'Fin rot',
  'White spots',
  'Ulcers/lesions',
  'Bloating',
  'Eye cloudiness',
  'Gill discoloration',
  'Scale loss',
  'Hemorrhaging',
  'Mucus overproduction'
] as const

export type Symptom = typeof SYMPTOM_OPTIONS[number]

// Anomaly Category Labels (legacy, for displaying old analyses)
export const ANOMALY_CATEGORY_LABELS: Record<AnomalyCategory, string> = {
  water_quality: 'Water Quality',
  mortality: 'Mortality',
  growth: 'Growth Issues',
  behavioral: 'Behavioral Changes',
  equipment: 'Equipment',
  environmental: 'Environmental'
}

// Trigger Record Type Labels
export const TRIGGER_RECORD_TYPE_LABELS: Record<TriggerRecordType, string> = {
  water_quality: 'Water Quality Test',
  health_check: 'Health Check',
  feed_record: 'Feed Record',
  maintenance_check: 'Maintenance Check'
}

// Severity Colors for UI
export const SEVERITY_COLORS: Record<Severity, string> = {
  none: 'bg-emerald-100 text-emerald-800',
  low: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800'
}

// Status Colors for UI
export const STATUS_COLORS: Record<AnalysisStatus, string> = {
  pending: 'bg-gray-100 text-gray-800',
  analyzing: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800'
}
